import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { type ReactElement, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useBuildByIdQuery, useDeleteBuild, useUpdateBuild } from '@/features/builds/hooks/builds';
import { buildJobDetailPath } from '@/features/jobs/utils/jobPaths';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { JobExpandedRuns } from '@/features/pipelines/components/PipelineTableV2NestedJobRuns';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { buildStageDetailPath, buildSyntheticStageId } from '@/features/pipelines/utils/stagePaths';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildEntityMetricColgroup } from '@/features/shared/components/tableSystem/TableV2MetricColumnLayout';
import { TableV2MetricCells } from '@/features/shared/components/tableSystem/TableV2MetricColumns';
import { tableV2NestedCarrierRowSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2ProjectCell,
  TableV2SortableHeader,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { buildTagColorByName } from '@/features/shared/tags';
import { resolvePreferredCiDisplayStatus } from '@/features/shared/utils/ciStatus';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { queryKeys } from '@/shared/hooks';
import { resolveVisibleMetricThresholdKeys } from '@/shared/utils/metricsThresholds';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { JobListItemDto, MetricThresholdGroup, QueryResult } from '@flowqa/shared';

export type JobTableV2SortKey =
  | 'jobName'
  | 'runAverageDurationMs'
  | 'runFrequencyPerDay'
  | 'runFlakeScore'
  | 'runPassRate'
  | 'runTotal'
  | 'startedAt'
  | 'status';

const metricSortByKey: Partial<Record<JobTableV2SortKey, string>> = {
  runAverageDurationMs: 'runAverageDurationMs',
  runFrequencyPerDay: 'runFrequencyPerDay',
  runFlakeScore: 'runFlakeScore',
  runPassRate: 'runPassRate',
};

const baseSkeletonColumns = ['chevron', 'name', 'status', 'dateTime', 'tags', 'actions'] as const;

const buildSkeletonColumns = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject: boolean,
): TableV2SkeletonColumnKind[] => [
  ...baseSkeletonColumns.slice(0, 4),
  ...visibleMetricKeys.map(() => 'metric' as const),
  ...baseSkeletonColumns.slice(4, 5),
  ...(includeProject ? (['project'] satisfies TableV2SkeletonColumnKind[]) : []),
  ...baseSkeletonColumns.slice(5),
];

const buildColgroup = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject: boolean,
): ReactElement => buildEntityMetricColgroup(visibleMetricKeys, includeProject);

type ProjectOption = { value: string; label: string };

const toMetrics = (row: JobListItemDto): NonNullable<JobListItemDto['metrics']> | null =>
  row.metrics
    ? {
        ...row.metrics,
        averageDurationMs: row.metrics.averageDurationMs,
        frequencyPerDay: row.metrics.frequencyPerDay,
        passRate: row.metrics.passRate,
      }
    : null;

type JobsTableV2Props = {
  active?: boolean;
  initialPageSize?: number;
  jobRunsEndAt?: string | null;
  jobRunsStartAt?: string | null;
  noRowsText: string;
  onQueryMeta?: (meta: { total: number; items: JobListItemDto[] } | null) => void;
  pageSizeOptions?: number[];
  projectOptions?: ProjectOption[];
  queryFn: (input: GridQueryInput) => Promise<QueryResult<JobListItemDto>>;
  queryKey: QueryKey;
  search?: string;
  showProjectColumn?: boolean;
  sort: ApiSort;
  tagOptionsOverride?: TagOption[];
  onSortChange: (sort: ApiSort) => void;
};

const JobRow = ({
  deleteBuildPending,
  editPending,
  isLastVisibleRow,
  jobRunsEndAt,
  jobRunsStartAt,
  projectName,
  row,
  showProjectColumn,
  tagColorByName,
  thresholds,
  visibleMetricKeys,
  onDeleteRow,
  onEditRow,
}: {
  deleteBuildPending: boolean;
  editPending: boolean;
  isLastVisibleRow: boolean;
  jobRunsEndAt: string | null;
  jobRunsStartAt: string | null;
  projectName: string | null;
  row: JobListItemDto;
  showProjectColumn: boolean;
  tagColorByName: Partial<Record<string, TagColor>>;
  thresholds: MetricThresholdGroup;
  visibleMetricKeys: MetricThresholdGroupKey[];
  onDeleteRow: (row: JobListItemDto) => void;
  onEditRow: (row: JobListItemDto) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const pipelineHref =
    row.pipelineName && row.pipelineId
      ? `/pipelines/${resolvePipelineSlug({ id: row.pipelineId, name: row.pipelineName })}`
      : null;
  const stageHref = row.stageName
    ? buildStageDetailPath(buildSyntheticStageId(row.pipelineId, row.stageName))
    : null;
  const secondaryItems = [
    row.pipelineName ? { href: pipelineHref, label: row.pipelineName } : null,
    row.stageName ? { href: stageHref, label: row.stageName } : null,
  ].filter((item): item is { href: string | null; label: string } => item !== null);

  return (
    <>
      <TableRow
        sx={buildTableV2BodyRowSurfaceSx({
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse job runs' : 'Expand job runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={buildJobDetailPath(row.id)}
            primary={row.jobName}
            secondaryItems={secondaryItems}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip
            status={resolvePreferredCiDisplayStatus({
              status: row.status,
              lifecycleState: row.lifecycleState ?? row.latestRun?.lifecycleState ?? null,
              conclusion: row.conclusion ?? row.latestRun?.conclusion ?? null,
              requiresManualAction:
                row.requiresManualAction ?? row.latestRun?.requiresManualAction ?? null,
            })}
          />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.latestRun?.timestamp ?? row.lastUpdatedAt} />
        </TableCell>
        <TableV2MetricCells
          metrics={toMetrics(row)}
          thresholds={thresholds}
          visibleMetricKeys={visibleMetricKeys}
        />
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        {showProjectColumn ? (
          <TableCell>
            <TableV2ProjectCell project={null} fallbackName={projectName} />
          </TableCell>
        ) : null}
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={row.jobName}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Job')}
            deleteActionLabel={getEntityActionLabel('delete', 'Job')}
            showEditTagsAction={false}
            onEdit={
              row.latestRun
                ? () => {
                    onEditRow(row);
                  }
                : undefined
            }
            onDelete={
              row.latestRun
                ? () => {
                    onDeleteRow(row);
                  }
                : undefined
            }
            disableEdit={row.latestRun === null || editPending}
            disableDelete={row.latestRun === null || deleteBuildPending}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? { '& > td': { borderBottom: (theme) => `1px solid ${theme.app.colors.border}` } }
            : {},
        ]}
      >
        <TableCell colSpan={buildSkeletonColumns(visibleMetricKeys, showProjectColumn).length}>
          <JobExpandedRuns
            childCount={row.runTotal}
            expanded={expanded}
            jobName={row.jobName}
            organisationId={row.organisationId}
            pipelineId={row.pipelineId}
            projectId={row.projectId}
            stageName={row.stageName}
            jobRunsEndAt={jobRunsEndAt}
            jobRunsStartAt={jobRunsStartAt}
            tagColorByName={tagColorByName}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const JobsTableV2 = ({
  active = true,
  initialPageSize = 10,
  jobRunsEndAt = null,
  jobRunsStartAt = null,
  noRowsText,
  onQueryMeta,
  onSortChange,
  pageSizeOptions = [10, 25, 50],
  projectOptions = [],
  queryFn,
  queryKey,
  search,
  showProjectColumn = false,
  sort,
  tagOptionsOverride = [],
}: JobsTableV2Props): ReactElement => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const updateBuild = useUpdateBuild();
  const deleteBuild = useDeleteBuild();
  const [editTarget, setEditTarget] = useState<JobListItemDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JobListItemDto | null>(null);
  const editTargetRunQuery = useBuildByIdQuery(editTarget?.latestRun?.id ?? null);
  const editTargetRun = editTargetRunQuery.data;
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const editEditor = useDetailEditEditor<JobListItemDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Job updated',
    saveEntity: async ({ tags, status, timestamp, secondaryTimestamp, durationMs }) => {
      const latestRunId = editTargetRun?.id ?? editTarget?.latestRun?.id ?? null;
      if (!latestRunId) return;
      const startedAt = timestamp ? timestamp.toISOString() : null;
      const finishedAt =
        secondaryTimestamp !== undefined
          ? (secondaryTimestamp?.toISOString() ?? null)
          : timestamp && durationMs !== null && durationMs !== undefined
            ? new Date(timestamp.getTime() + durationMs).toISOString()
            : null;
      await updateBuild.mutateAsync({
        id: latestRunId,
        tags,
        status,
        startedAt,
        finishedAt,
      });
    },
  });
  const visibleMetricKeys = useMemo(
    () => resolveVisibleMetricThresholdKeys(thresholds.jobs),
    [thresholds.jobs],
  );
  const projectNameById = useMemo(
    () => new Map(projectOptions.map((project) => [project.value, project.label])),
    [projectOptions],
  );
  const tagColorByName = useMemo(
    () => buildTagColorByName(tagOptionsOverride),
    [tagOptionsOverride],
  );
  const handleSortChange = (field: JobTableV2SortKey): void => {
    onSortChange({
      field,
      direction: sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc',
    });
  };
  const invalidateJobViews = async (jobId: string | null): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobs(selectedProjectId, selectedOrganisationId ?? null),
      }),
      ...(jobId
        ? [
            queryClient.invalidateQueries({
              queryKey: queryKeys.jobDetail(jobId, selectedOrganisationId ?? null),
            }),
          ]
        : []),
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'datagrid' &&
          typeof query.queryKey[1] === 'string' &&
          query.queryKey[1].startsWith('jobs'),
      }),
      queryClient.invalidateQueries({ queryKey: queryKeys.metricsPrefix() }),
    ]);
  };
  const header = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="jobName"
          label="Job"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="status"
          label="Latest Status"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="startedAt"
          label="Ran At"
          onSortChange={handleSortChange}
        />
      </TableCell>
      {visibleMetricKeys.includes('passRate') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="runPassRate"
            label="Success Rate"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {visibleMetricKeys.includes('frequency') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="runFrequencyPerDay"
            label="Frequency"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {visibleMetricKeys.includes('avgDuration') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="runAverageDurationMs"
            label="Avg. Duration"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {visibleMetricKeys.includes('flakeScore') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="runFlakeScore"
            label="Flake Rate"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      <TableCell>
        <TableV2HeaderLabel label="Tags" />
      </TableCell>
      {showProjectColumn ? (
        <TableCell>
          <TableV2HeaderLabel label="Project" />
        </TableCell>
      ) : null}
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Jobs table"
        colgroup={buildColgroup(visibleMetricKeys, showProjectColumn)}
        emptyText={noRowsText}
        entityLabel="jobs"
        entitySingularLabel="job"
        header={header}
        initialPageSize={initialPageSize}
        onQueryMeta={onQueryMeta}
        pageSizeOptions={pageSizeOptions}
        queryFn={queryFn}
        queryKey={queryKey}
        renderRow={(row, { isLastVisibleRow }) => (
          <JobRow
            key={row.id}
            deleteBuildPending={deleteBuild.isPending}
            editPending={editEditor.isPending}
            isLastVisibleRow={isLastVisibleRow}
            jobRunsEndAt={jobRunsEndAt}
            jobRunsStartAt={jobRunsStartAt}
            projectName={projectNameById.get(row.projectId) ?? null}
            row={row}
            showProjectColumn={showProjectColumn}
            tagColorByName={tagColorByName}
            thresholds={thresholds.jobs}
            visibleMetricKeys={visibleMetricKeys}
            onDeleteRow={(nextRow) => {
              setDeleteTarget(nextRow);
            }}
            onEditRow={(nextRow) => {
              setEditTarget(nextRow);
            }}
          />
        )}
        search={search}
        skeletonColumns={buildSkeletonColumns(visibleMetricKeys, showProjectColumn)}
        sorts={[{ ...sort, field: metricSortByKey[sort.field as JobTableV2SortKey] ?? sort.field }]}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Job"
        entityName={editTarget?.jobName ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        status={editTarget?.status}
        statusOptions={[
          { value: 'queued', label: 'Queued' },
          { value: 'running', label: 'Running' },
          { value: 'completed', label: 'Completed' },
          { value: 'failed', label: 'Failed' },
        ]}
        timestamp={editTargetRun?.startedAt ?? null}
        secondaryTimestamp={editTargetRun?.finishedAt ?? null}
        durationMs={
          editTargetRun?.startedAt && editTargetRun.finishedAt
            ? Math.max(editTargetRun.finishedAt.getTime() - editTargetRun.startedAt.getTime(), 0)
            : null
        }
        timestampLabel="Started"
        secondaryTimestampLabel="Finished"
        durationLabel="Duration"
        durationInputMode="timeParts"
        loading={editEditor.isPending || (editTarget !== null && editTargetRunQuery.isPending)}
        cascadeCopy="Changes apply to the latest run shown for this job."
        onClose={() => {
          if (editEditor.isPending) return;
          setEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, secondaryTimestamp, durationMs }) => {
          try {
            await editEditor.save({
              tags,
              colors,
              status,
              timestamp,
              secondaryTimestamp,
              durationMs,
            });
            await invalidateJobViews(editTarget?.id ?? null);
            setEditTarget(null);
          } catch (error) {
            notify({ message: toErrorMessage(error), tone: 'red' });
          }
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'job' })}
        description="This deletes the latest run shown for this job."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'job' })}
        confirmColor="red"
        loading={deleteBuild.isPending}
        onClose={() => {
          if (deleteBuild.isPending) return;
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          const latestRunId = deleteTarget?.latestRun?.id ?? null;
          if (!latestRunId || !deleteTarget) return;
          const deletedJobName = deleteTarget.jobName;
          deleteBuild.mutate(
            { id: latestRunId, projectId: deleteTarget.projectId },
            {
              onSuccess: async () => {
                await invalidateJobViews(deleteTarget.id);
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'job',
                    name: deletedJobName,
                  }),
                  tone: 'green',
                });
                setDeleteTarget(null);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};
